import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { MdClose, MdDiamond } from "react-icons/md";
import { HiMenuAlt2, HiOutlineMenuAlt4 } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaSearch,
  FaUser,
  FaUserEdit,
  FaCaretDown,
  FaShoppingCart,
  FaGem,
  FaCrown,
  FaStar,
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { logo } from "../../../assets/images";
import Image from "../../designLayouts/Image";
import { navBarList, allItems } from "../../../constants";
import Flex from "../../designLayouts/Flex";
import { useContextSelector } from "use-context-selector";
import { StateContext } from "../../../contexts/StateContext";
import {
  ADMIN,
  PUPIL,
  ACCESS_TOKEN,
  ADMIN_TOKEN,
} from "../../../config/Config";
import toast, { Toaster } from "react-hot-toast";

const Header = () => {
  // State management
  const [showMenu, setShowMenu] = useState(true);
  const [sidenav, setSidenav] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [popup, setPopup] = useState({ type: null, isVisible: false });
  const [scrolled, setScrolled] = useState(false);

  // Context and hooks
  const setRole = useContextSelector(StateContext, (state) => state.setRole);
  const role = useContextSelector(StateContext, (state) => state.role);
  const setIsLoggedIn = useContextSelector(
    StateContext,
    (state) => state.setIsLoggedIn
  );
  const products = useSelector((state) => state.orebiReducer.products);
  const location = useLocation();
  const navigate = useNavigate();

  // Refs
  const categoryRef = useRef(null);
  const userRef = useRef(null);
  const searchRef = useRef(null);

  // Clean Professional Color Palette
  const colors = {
    primary: "#FFFFFF", // Pure white
    secondary: "#F8FAFC", // Light gray background
    tertiary: "#E2E8F0", // Border gray
    accent: "#2563EB", // Professional blue
    accentLight: "#3B82F6", // Light blue
    accentDark: "#1D4ED8", // Dark blue
    text: "#1E293B", // Dark text
    textSecondary: "#475569", // Medium text
    textMuted: "#64748B", // Muted text
    border: "#CBD5E1", // Light border
    borderLight: "#E2E8F0", // Lighter border
    success: "#10B981",
    error: "#EF4444",
    gradient: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)"
  };

  // Scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      setScrolled(isScrolled);
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Responsive menu handling
  useEffect(() => {
    const ResponsiveMenu = () => {
      setShowMenu(window.innerWidth >= 768);
    };
    
    ResponsiveMenu();
    window.addEventListener("resize", ResponsiveMenu);
    return () => window.removeEventListener("resize", ResponsiveMenu);
  }, []);

  // Search functionality
  useEffect(() => {
    const filtered = allItems.filter((item) =>
      item.productName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProducts(filtered);
  }, [searchQuery]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popup.isVisible) {
        if (popup.type === "category" && categoryRef.current && !categoryRef.current.contains(event.target)) {
          setPopup({ type: null, isVisible: false });
        }
        if (popup.type === "user" && userRef.current && !userRef.current.contains(event.target)) {
          setPopup({ type: null, isVisible: false });
        }
      }
      if (searchQuery && searchRef.current && !searchRef.current.contains(event.target)) {
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [popup, searchQuery]);

  // Authentication
  const isAuthenticated = useCallback(() => {
    if (localStorage.getItem(ACCESS_TOKEN)) {
      let token = localStorage.getItem(ACCESS_TOKEN);
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace("-", "+").replace("_", "/");
      token = JSON.parse(window.atob(base64));
      
      if (token.exp <= Math.floor(Date.now() / 1000)) {
        localStorage.removeItem(ACCESS_TOKEN);
        localStorage.removeItem(ADMIN_TOKEN);
        setIsLoggedIn(false);
        return false;
      }

      if (token.rol?.length >= 1) {
        if (token.rol[0] === "ADMIN") {
          localStorage.setItem(ADMIN_TOKEN, token.rol[0]);
          setRole(token.rol[0]);
        } else {
          setRole(token.rol[0]);
        }
        return true;
      }
    }
    return false;
  }, [setIsLoggedIn, setRole]);

  // Logout functionality
  const logout = () => {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ADMIN_TOKEN);
    setIsLoggedIn(false);
    setPopup({ type: null, isVisible: false });
    setRole("");
    toast.success("Logged out successfully");
    navigate("/");
  };

  // Search handlers
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleProductClick = (item) => {
    const formattedProductName = item.productName
      .toLowerCase()
      .split(" ")
      .join("-");
    navigate(`/product/${formattedProductName}`, {
      state: { item: item },
    });
    setSearchQuery("");
  };

  // Enhanced categories with jewelry-specific items
  const categories = [
    { name: "Gold Collection", path: "Gold", icon: <FaCrown className="text-lg" /> },
    { name: "Silver Elegance", path: "Silver", icon: <MdDiamond className="text-lg" /> },
    { name: "Diamond Rings", path: "Diamond", icon: <FaGem className="text-lg" /> },
    { name: "Platinum Range", path: "Platinum", icon: <FaStar className="text-lg" /> },
    { name: "Bridal Collection", path: "Bridal", icon: <FaGem className="text-lg" /> },
  ];

  // User menu based on role
  const renderUserMenu = () => {
    if (role === ADMIN) {
      return (
        <>
          <Link to="/adminDashboard" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <FaUser className="mr-3 text-blue-500" />
                <span>Dashboard</span>
              </div>
            </li>
          </Link>
          <Link to="/product" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <FaGem className="mr-3 text-blue-500" />
                <span>Product Management</span>
              </div>
            </li>
          </Link>
          <Link to="/inventory" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <span>Inventory</span>
              </div>
            </li>
          </Link>
           <Link to="/addVendor" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <span>Ventor Management</span>
              </div>
            </li>
          </Link>
          <Link to="/signup" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <FaUserEdit className="mr-3 text-blue-500" />
                <span>My Profile</span>
              </div>
            </li>
          </Link>
          <button onClick={logout} className="w-full text-left">
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group">
              <div className="flex items-center text-gray-700 group-hover:text-red-600">
                <span>Logout</span>
              </div>
            </li>
          </button>
        </>
      );
    } else if (role === PUPIL) {
      return (
        <>
          <Link to="/profile" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <FaUser className="mr-3 text-blue-500" />
                <span>My Profile</span>
              </div>
            </li>
          </Link>
          <button onClick={logout} className="w-full text-left">
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group">
              <div className="flex items-center text-gray-700 group-hover:text-red-600">
                <span>Log out</span>
              </div>
            </li>
          </button>
        </>
      );
    } else {
      return (
        <>
          <Link to="/signin" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <span>Login</span>
              </div>
            </li>
          </Link>
          <Link to="/signup" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group border-b border-gray-100">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <span>Sign Up</span>
              </div>
            </li>
          </Link>
          <Link to="/Chit" onClick={() => setPopup({ type: null, isVisible: false })}>
            <li className="px-4 py-3 hover:bg-blue-50 transition-all duration-300 cursor-pointer group">
              <div className="flex items-center text-gray-700 group-hover:text-blue-600">
                <span>Chit Scheme</span>
              </div>
            </li>
          </Link>
        </>
      );
    }
  };

  return (
    <>
      {/* Clean Professional Header */}
      <div 
        className={`w-full fixed top-0 z-50 transition-all duration-500 ${
          scrolled 
            ? "bg-white shadow-lg border-b border-gray-200" 
            : "bg-white"
        }`}
      >
        
        {/* Main Navigation */}
        <nav className="h-20 px-4 max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-full">
            
            {/* Logo Section */}
            <Link to="/" className="flex items-center group flex-shrink-0">
              <Image 
                className="w-20 object-cover transform group-hover:scale-105 transition-transform duration-300" 
                imgSrc={logo} 
              />
              <div className="ml-3">
                <h1 
                  className="font-bold text-2xl md:text-3xl font-serif italic tracking-wider"
                  style={{
                    background: "linear-gradient(45deg, #2563EB, #3B82F6, #60A5FA, #2563EB)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundSize: "300% 300%",
                    animation: "shimmer 3s ease-in-out infinite",
                  }}
                >
                  Palanigounder
                </h1>
                <p className="text-xs text-blue-600 tracking-widest font-light mt-1 opacity-90">LUXURY JEWELS</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            {showMenu && (
              <motion.ul
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="hidden lg:flex items-center space-x-1"
              >
                {navBarList.map(({ _id, title, link }) => (
                  <NavLink
                    key={_id}
                    className={({ isActive }) => 
                      `relative px-6 py-2 text-sm font-medium transition-all duration-300 rounded-full group
                      ${isActive 
                        ? "text-blue-600 bg-blue-50 shadow-sm" 
                        : "text-gray-700 hover:text-blue-600 hover:bg-blue-50"
                      }`
                    }
                    to={link}
                  >
                    <li className="tracking-wide flex items-center">
                      {title}
                      {title === "New Arrivals" && (
                        <span className="ml-2 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full animate-pulse">
                          New
                        </span>
                      )}
                    </li>
                  </NavLink>
                ))}
              </motion.ul>
            )}

            {/* User & Cart Section */}
            <div className="flex items-center space-x-3">
              
              {/* Search Icon - Mobile */}
              {/* <div className="lg:hidden">
                <button className="p-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 hover:bg-blue-50 rounded-lg">
                  <FaSearch className="w-5 h-5" />
                </button>
              </div> */}

              {/* User Menu */}
              <div ref={userRef} className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setPopup({ 
                    type: popup.type === "user" ? null : "user", 
                    isVisible: !popup.isVisible 
                  })}
                  className="flex items-center space-x-2 p-2 rounded-lg transition-all duration-300 hover:bg-blue-50 border border-transparent hover:border-blue-200"
                >
                  {role === ADMIN || role === PUPIL ? (
                    <FaUser className="w-5 h-5 text-blue-500" />
                  ) : (
                    <FaUserEdit className="w-5 h-5 text-blue-500" />
                  )}
                  <FaCaretDown className={`transition-transform duration-300 text-gray-500 ${
                    popup.isVisible && popup.type === "user" ? "rotate-180 text-blue-600" : ""
                  }`} />
                </motion.button>

                <AnimatePresence>
                  {popup.isVisible && popup.type === "user" && (
                    <motion.ul
                      initial={{ y: 10, opacity: 0, scale: 0.95 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      exit={{ y: 10, opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-12 right-0 z-50 w-64 rounded-lg overflow-hidden border border-gray-200 shadow-xl bg-white"
                    >
                      {renderUserMenu()}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              {/* Shopping Cart */}
              <Link 
                to="/cart" 
                className="relative p-2 rounded-lg transition-all duration-300 hover:bg-blue-50 border border-transparent hover:border-blue-200 group"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FaShoppingCart className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
                </motion.div>
                {products.length > 0 && (
                  <motion.span 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full border border-white shadow-lg"
                  >
                    {products.length}
                  </motion.span>
                )}
              </Link>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setSidenav(true)}
                className="lg:hidden p-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 rounded-lg hover:bg-blue-50"
              >
                <HiMenuAlt2 className="w-6 h-6" />
              </button>
            </div>
          </div>
        </nav>

        {/* Bottom Bar - Search & Categories - Blue Background */}
        <div 
          className="border-t border-blue-300 py-3"
          style={{ background: "linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)" }}
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col lg:flex-row items-center justify-between space-y-3 lg:space-y-0">
              
              {/* Categories Dropdown */}
              <div
                ref={categoryRef}
                className="flex items-center space-x-3 cursor-pointer group"
                onClick={() => setPopup({ 
                  type: popup.type === "category" ? null : "category", 
                  isVisible: !popup.isVisible 
                })}
              >
                <motion.div 
                  whileHover={{ scale: 1.05 }}
                  className="p-2 rounded-lg bg-white/20 group-hover:bg-white/30 transition-all duration-300 border border-white/30 group-hover:border-white/50"
                >
                  <HiOutlineMenuAlt4 className="w-5 h-5 text-white" />
                </motion.div>
                <div>
                  <p className="text-sm font-semibold text-white tracking-wide">SHOP COLLECTIONS</p>
                  <p className="text-xs text-blue-100">Discover Luxury Pieces</p>
                </div>
                
                <AnimatePresence>
                  {popup.isVisible && popup.type === "category" && (
                    <motion.ul
                      initial={{ y: 10, opacity: 0, scale: 0.95 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      exit={{ y: 10, opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-16 left-4 z-50 w-80 rounded-lg overflow-hidden border border-gray-200 shadow-xl bg-white"
                    >
                      {categories.map((category, index) => (
                        <motion.div
                          key={category.path}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Link 
                            to="/shop"
                            state={{ category: category.path }}
                            onClick={() => setPopup({ type: null, isVisible: false })}
                            className="block transition-all duration-200 hover:bg-blue-50"
                          >
                            <li className="px-6 py-4 border-b border-gray-100 last:border-b-0 group">
                              <div className="flex items-center space-x-4">
                                <div className="text-blue-500 group-hover:scale-110 transition-transform duration-300">
                                  {category.icon}
                                </div>
                                <div>
                                  <p className="text-gray-800 font-medium group-hover:text-blue-600 transition-colors duration-300">
                                    {category.name}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">Exclusive collection</p>
                                </div>
                              </div>
                            </li>
                          </Link>
                        </motion.div>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </div>

              {/* Search Bar */}
              <div ref={searchRef} className="flex-1 max-w-2xl w-full">
                <div className="relative group">
                  <input
                    className="w-full h-12 px-5 pr-12 outline-none text-gray-800 placeholder-gray-500 text-sm bg-white rounded-full border border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-300 shadow-sm"
                    type="text"
                    onChange={handleSearch}
                    value={searchQuery}
                    placeholder="🔍 Search for diamonds, gold, pearls, and exclusive jewelry..."
                  />
                  <FaSearch className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-500" />
                </div>

                {/* Search Results */}
                <AnimatePresence>
                  {searchQuery && filteredProducts.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute top-16 left-0 right-0 z-50 max-h-96 overflow-y-auto rounded-lg border border-gray-200 shadow-xl bg-white"
                    >
                      <div className="p-3 border-b border-gray-100">
                        <p className="text-sm text-blue-600 font-semibold">
                          Found {filteredProducts.length} products
                        </p>
                      </div>
                      {filteredProducts.map((item, index) => (
                        <motion.div
                          key={item._id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          onClick={() => handleProductClick(item)}
                          className="flex items-center space-x-4 p-4 border-b border-gray-100 last:border-b-0 hover:bg-blue-50 cursor-pointer group transition-all duration-300"
                        >
                          <img 
                            className="w-16 h-16 object-cover rounded-lg border border-gray-200 group-hover:border-blue-300 transition-colors duration-300" 
                            src={item?.img[0]} 
                            alt={item.productName}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800 text-sm group-hover:text-blue-600 transition-colors duration-300 truncate">
                              {item.productName}
                            </p>
                            <p className="text-xs text-gray-500 truncate mt-1">
                              {item.des}
                            </p>
                            <p className="text-sm font-bold text-blue-600 mt-2">
                              ₹{item.price}
                            </p>
                          </div>
                          
                        </motion.div>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Side Navigation */}
      <AnimatePresence>
        {sidenav && (
          <div className="fixed inset-0 z-50">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setSidenav(false)}
            />
            <motion.div
              initial={{ x: -320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -320, opacity: 0 }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="relative w-80 h-full shadow-2xl border-r border-gray-200 bg-white"
            >
              <div className="flex flex-col h-full">
                {/* Mobile Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                  <div className="flex items-center">
                    <img className="w-16" src={logo} alt="Palanigounder Jewels" />
                    <div className="ml-3">
                      <h1 className="text-blue-600 font-serif italic text-lg">Palanigounder</h1>
                      <p className="text-xs text-gray-500">LUXURY JEWELS</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSidenav(false)}
                    className="p-2 text-gray-500 hover:text-blue-600 transition-colors duration-200 rounded-lg hover:bg-blue-50"
                  >
                    <MdClose className="w-6 h-6" />
                  </button>
                </div>

                {/* Mobile Navigation */}
                <div className="flex-1 overflow-y-auto p-4">
                  <ul className="space-y-2">
                    {navBarList.map((item) => (
                      <li key={item._id}>
                        <NavLink
                          to={item.link}
                          onClick={() => setSidenav(false)}
                          className={({ isActive }) => 
                            `block py-3 px-4 rounded-lg transition-all duration-200 font-medium
                            ${isActive 
                              ? "bg-blue-50 text-blue-600 border-l-4 border-blue-500 shadow-sm" 
                              : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                            }`
                          }
                        >
                          {item.title}
                        </NavLink>
                      </li>
                    ))}
                  </ul>

                  {/* Mobile Categories */}
                  <div className="mt-8">
                    <h3 className="px-4 py-3 text-sm font-semibold text-blue-600 uppercase tracking-wide border-b border-gray-200">
                      Luxury Collections
                    </h3>
                    <div className="grid grid-cols-1 gap-2 mt-3">
                      {categories.map((category) => (
                        <Link
                          key={category.path}
                          to="/shop"
                          state={{ category: category.path }}
                          onClick={() => setSidenav(false)}
                          className="flex items-center py-3 px-4 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-all duration-200 group"
                        >
                          <span className="text-blue-500 mr-3 group-hover:scale-110 transition-transform duration-300">
                            {category.icon}
                          </span>
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Mobile Footer */}
                <div className="p-4 border-t border-gray-200">
                  <div className="text-center text-gray-500 text-sm">
                    <p>✨ Premium Jewelry Since 1985</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notifications */}
      <Toaster
        position="top-right"
        reverseOrder={false}
        toastOptions={{
          duration: 4000,
          style: {
            background: '#FFFFFF',
            color: '#1E293B',
            border: '1px solid #E2E8F0',
            borderRadius: '12px',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
          },
          success: {
            iconTheme: {
              primary: '#10B981',
              secondary: '#FFFFFF',
            },
          },
          error: {
            style: {
              background: '#FFFFFF',
              color: '#1E293B',
              border: '1px solid #FECACA',
            },
          },
        }}
      />

      {/* Spacer for fixed header */}
      <div className="h-32"></div>

      {/* Add shimmer animation */}
      <style jsx>{`
        @keyframes shimmer {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </>
  );
};

export default Header;